import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AuditLogs.css';

const AuditLogs = () => {
  const [view, setView] = useState('files'); // 'files', 'recent', 'summary'
  const [logFiles, setLogFiles] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filters, setFilters] = useState({
    q: '',
    actionType: '',
    itemType: '',
    startDate: '',
    endDate: ''
  });
  const [searchResults, setSearchResults] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

  useEffect(() => {
    if (view === 'files') {
      fetchLogFiles();
    } else if (view === 'recent') {
      fetchRecentActivity();
    } else if (view === 'summary') {
      fetchSummary();
    }
  }, [view]);

  const fetchLogFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/audit-logs/my-district/files');
      setLogFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch log files:', error);
      alert('Failed to load log files');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/audit-logs/my-district/recent?limit=100');
      setRecentActivity(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      alert('Failed to load recent activity');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/audit-logs/my-district/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      alert('Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await axios.get(`/api/audit-logs/my-district/files/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download log file');
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/audit-logs/my-district/files/${filename}`);
      fetchLogFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete log file');
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      const response = await axios.get(`/api/audit-logs/my-district/search?${params}`);
      setSearchResults(response.data.entries || []);
      setPagination({ ...pagination, total: response.data.total });
    } catch (error) {
      console.error('Failed to search logs:', error);
      alert('Failed to search logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({
        format,
        ...filters
      });
      const response = await axios.get(`/api/audit-logs/my-district/export?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export logs:', error);
      alert('Failed to export logs');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getLogTypeColor = (logType) => {
    const colors = {
      update: '#3498db',
      delete: '#e74c3c',
      account_creation: '#2ecc71',
      backup: '#9b59b6',
      failed_operation: '#f39c12',
      audit_log_access: '#95a5a6'
    };
    return colors[logType] || '#34495e';
  };

  return (
    <div className="audit-logs-container">
      <div className="audit-logs-header">
        <h2>Audit Logs</h2>
        <div className="view-tabs">
          <button 
            className={view === 'files' ? 'active' : ''} 
            onClick={() => setView('files')}
          >
            Log Files
          </button>
          <button 
            className={view === 'recent' ? 'active' : ''} 
            onClick={() => setView('recent')}
          >
            Recent Activity
          </button>
          <button 
            className={view === 'summary' ? 'active' : ''} 
            onClick={() => setView('summary')}
          >
            Summary
          </button>
        </div>
      </div>

      {view === 'files' && (
        <div className="log-files-view">
          <div className="actions-bar">
            <button onClick={fetchLogFiles} disabled={loading}>
              Refresh
            </button>
            {selectedFiles.length > 0 && (
              <button 
                onClick={() => {
                  selectedFiles.forEach(filename => handleDownload(filename));
                }}
              >
                Download Selected ({selectedFiles.length})
              </button>
            )}
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>
                    <input 
                      type="checkbox" 
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFiles(logFiles.map(f => f.filename));
                        } else {
                          setSelectedFiles([]);
                        }
                      }}
                    />
                  </th>
                  <th>Date</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Lines</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logFiles.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center' }}>No log files found</td>
                  </tr>
                ) : (
                  logFiles.map((file) => (
                    <tr key={file.filename}>
                      <td>
                        <input 
                          type="checkbox"
                          checked={selectedFiles.includes(file.filename)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFiles([...selectedFiles, file.filename]);
                            } else {
                              setSelectedFiles(selectedFiles.filter(f => f !== file.filename));
                            }
                          }}
                        />
                      </td>
                      <td>{file.date}</td>
                      <td>{file.filename}</td>
                      <td>{formatBytes(file.size_bytes)}</td>
                      <td>{file.line_count}</td>
                      <td>
                        <button onClick={() => handleDownload(file.filename)}>Download</button>
                        <button onClick={() => handleDelete(file.filename)} className="btn-danger">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === 'recent' && (
        <div className="recent-activity-view">
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
            <select
              value={filters.actionType}
              onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
            >
              <option value="">All Actions</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="account_creation">Account Creation</option>
              <option value="backup">Backup</option>
              <option value="failed_operation">Failed Operation</option>
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              placeholder="End Date"
            />
            <button onClick={handleSearch}>Search</button>
            <button onClick={() => handleExport('csv')}>Export CSV</button>
            <button onClick={() => handleExport('json')}>Export JSON</button>
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              {searchResults !== null ? (
                <div className="search-results">
                  <h3>Search Results ({pagination.total} total)</h3>
                  <div className="activity-list">
                    {searchResults.map((entry) => (
                      <div key={entry.id} className="activity-entry" style={{ borderLeftColor: getLogTypeColor(entry.log_type) }}>
                        <div className="activity-header">
                          <span className="log-type" style={{ color: getLogTypeColor(entry.log_type) }}>
                            {entry.log_type.toUpperCase()}
                          </span>
                          <span className="timestamp">{formatDate(entry.created_at)}</span>
                        </div>
                        <div className="activity-description">{entry.action_description}</div>
                        <div className="activity-details">
                          <span>User: {entry.username}</span>
                          <span>IP: {entry.ip_address}</span>
                          {entry.item_type && <span>Item: {entry.item_type}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="activity-list">
                  {recentActivity.map((entry) => (
                    <div key={entry.id} className="activity-entry" style={{ borderLeftColor: getLogTypeColor(entry.log_type) }}>
                      <div className="activity-header">
                        <span className="log-type" style={{ color: getLogTypeColor(entry.log_type) }}>
                          {entry.log_type.toUpperCase()}
                        </span>
                        <span className="timestamp">{formatDate(entry.created_at)}</span>
                      </div>
                      <div className="activity-description">{entry.action_description}</div>
                      <div className="activity-details">
                        <span>User: {entry.username}</span>
                        <span>IP: {entry.ip_address}</span>
                        {entry.item_type && <span>Item: {entry.item_type}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {view === 'summary' && summary && (
        <div className="summary-view">
          <div className="summary-cards">
            <div className="summary-card">
              <h3>Total Actions</h3>
              <p className="summary-value">{summary.totalActions}</p>
            </div>
            {Object.entries(summary.byType || {}).map(([type, count]) => (
              <div key={type} className="summary-card">
                <h3>{type.charAt(0).toUpperCase() + type.slice(1)}</h3>
                <p className="summary-value">{count}</p>
              </div>
            ))}
          </div>

          <div className="summary-section">
            <h3>Most Active Users</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {summary.byUser.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.username}</td>
                    <td>{user.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="summary-section">
            <h3>Actions by Item Type</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item Type</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {summary.byItemType.map((item) => (
                  <tr key={item.itemType}>
                    <td>{item.itemType}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
